import type { IconName } from '@customerio/pluma-icons';
import type { ChannelDefinition } from './channel-definition';
import { CHANNEL_EVENT_PHRASES, type ChannelEvent } from './events';
import { emailChannel } from './channels/email';
import { inAppChannel } from './channels/in-app';
import { inboxChannel } from './channels/inbox';
import { lineChannel } from './channels/line';
import { liveNotificationChannel } from './channels/live-notification';
import { pushChannel } from './channels/push';
import { slackChannel } from './channels/slack';
import { smsChannel } from './channels/sms';
import { twilioChannel } from './channels/twilio';
import { urbanAirshipChannel } from './channels/urban-airship';
import { webhookChannel } from './channels/webhook';
import { whatsappChannel } from './channels/whatsapp';

/** Every messaging channel, keyed by id. Adding a channel is one file in `channels/` and one line here. */
export const CHANNEL_REGISTRY = {
  [emailChannel.id]: emailChannel,
  [webhookChannel.id]: webhookChannel,
  [twilioChannel.id]: twilioChannel,
  [smsChannel.id]: smsChannel,
  [urbanAirshipChannel.id]: urbanAirshipChannel,
  [slackChannel.id]: slackChannel,
  [pushChannel.id]: pushChannel,
  [inAppChannel.id]: inAppChannel,
  [inboxChannel.id]: inboxChannel,
  [lineChannel.id]: lineChannel,
  [whatsappChannel.id]: whatsappChannel,
  [liveNotificationChannel.id]: liveNotificationChannel,
} as const;

export type ChannelId = keyof typeof CHANNEL_REGISTRY;

/**
 * A deliberate subset of channels, for a surface that supports only some of
 * them: `ChannelSubset<'email' | 'sms' | 'whatsapp'>`. Every member must be a
 * registry id, so a typo or a removed channel fails to compile.
 */
export type ChannelSubset<T extends ChannelId> = T;

export const CHANNELS = Object.values(CHANNEL_REGISTRY);

const CHANNEL_IDS_BY_VALUE: ReadonlyMap<string, ChannelId> = new Map(
  CHANNELS.map((channel) => [channel.id, channel.id]),
);

/** The channel id a string names, or null for one that isn't a channel. */
export function toChannelId(value: string): ChannelId | null {
  return CHANNEL_IDS_BY_VALUE.get(value) ?? null;
}

/** The icon that stands for a channel. */
export function getChannelIcon(id: ChannelId): IconName {
  return CHANNEL_REGISTRY[id].icon;
}

/** The icon for a string that may name a channel, or `fallback` for one that doesn't. */
export function channelIconOrFallback(value: string, fallback: IconName): IconName {
  const id = toChannelId(value);
  return id === null ? fallback : getChannelIcon(id);
}

/** The definition of a channel. */
export function getChannel(id: ChannelId): ChannelDefinition {
  return CHANNEL_REGISTRY[id];
}

/** The definition for a string that may name a channel, or undefined for one that doesn't. */
export function findChannel(value: string): ChannelDefinition | undefined {
  const id = toChannelId(value);
  return id === null ? undefined : getChannel(id);
}

/** One type of a channel's activity log: `sent_sms` is the `sms` channel's `sent` event. */
export type ChannelActivity = {
  readonly logType: `${ChannelEvent}_${ChannelId}`;
  readonly channel: ChannelId;
  readonly event: ChannelEvent;
  /** How the activity feed names it: "Sent SMS". */
  readonly name: string;
};

/** Every channel's activity log types, in registry order. */
export const CHANNEL_ACTIVITIES: readonly ChannelActivity[] = CHANNELS.flatMap(({ id, noun, events }) =>
  events.map(
    (event): ChannelActivity => ({
      logType: `${event}_${id}`,
      channel: id,
      event,
      name: `${CHANNEL_EVENT_PHRASES[event]} ${noun}`,
    }),
  ),
);

const CHANNEL_ACTIVITIES_BY_LOG_TYPE: ReadonlyMap<string, ChannelActivity> = new Map(
  CHANNEL_ACTIVITIES.map((activity) => [activity.logType, activity]),
);

/** The channel activity a log type names, or undefined for one that isn't a channel's (`sent_action`, `event`). */
export function findChannelActivity(logType: string): ChannelActivity | undefined {
  return CHANNEL_ACTIVITIES_BY_LOG_TYPE.get(logType);
}
